// Main content area. Everything rendered here comes from the single list of
// already-authorized items (useWorkspaceData); this component only groups,
// filters and lays them out.
import React, { useMemo } from "react";
import { ArrowRight, Star } from "lucide-react";
import AppTile from "./AppTile";
import { CATEGORY, CATEGORY_LABELS, searchItems } from "../workspaceCategories";
import { CATEGORY_ICONS } from "../categoryIcons";

const Section = ({ id, title, icon: Icon, count, onViewAll, children, className = "cc-section" }) => (
  <section className={className} aria-labelledby={`cc-${id}-heading`}>
    <div className="cc-section__head">
      <h2 className="cc-section__title" id={`cc-${id}-heading`}>
        {Icon ? <Icon aria-hidden="true" /> : null}
        {title}
        {typeof count === "number" ? <span className="cc-section__count">{count}</span> : null}
      </h2>
      {onViewAll ? (
        <button type="button" className="cc-link" onClick={onViewAll} aria-label={`View all ${title}`}>
          View all <ArrowRight size={14} aria-hidden="true" />
        </button>
      ) : null}
    </div>
    {children}
  </section>
);

const WorkspaceSections = ({
  items,
  activeCategory,
  query,
  quickAccessItems,
  favoritableCount,
  favoriteIds,
  canFavorite,
  onOpen,
  onToggleFavorite,
  onSelectCategory,
}) => {
  const byCategory = useMemo(() => {
    const groups = {};
    items.forEach((item) => {
      (groups[item.category] = groups[item.category] || []).push(item);
    });
    return groups;
  }, [items]);

  const tileProps = { favoriteIds, canFavorite, onOpen, onToggleFavorite };
  const renderTile = (item, variant, extra = {}) => (
    <li key={item.id}>
      <AppTile
        item={item}
        variant={variant}
        favorite={favoriteIds.has(item.id)}
        canFavorite={tileProps.canFavorite}
        onOpen={tileProps.onOpen}
        onToggleFavorite={tileProps.onToggleFavorite}
        {...extra}
      />
    </li>
  );

  const trimmedQuery = query.trim();
  if (trimmedQuery) {
    const results = searchItems(items, trimmedQuery);
    return (
      <div className="cc-view" key="search">
        <Section id="results" title={`Results for “${trimmedQuery}”`} count={results.length}>
          {results.length > 0 ? (
            <ul className="cc-grid cc-grid--rows" aria-label="Search results">
              {results.map((item) => renderTile(item, "row", { showCategory: true }))}
            </ul>
          ) : (
            <p className="cc-empty" role="status">No dashboards, portals or pages match “{trimmedQuery}”.</p>
          )}
        </Section>
      </div>
    );
  }

  const show = (category) => activeCategory === CATEGORY.ALL || activeCategory === category;
  const viewAll = (category) => (activeCategory === CATEGORY.ALL ? () => onSelectCategory(category) : undefined);
  const list = (category) => byCategory[category] || [];

  const quickAccessSection = (
    <Section
      id="quick-access"
      title="Quick Access"
      icon={Star}
      className="cc-section cc-quick-panel"
      onViewAll={activeCategory === CATEGORY.ALL ? () => onSelectCategory(CATEGORY.FAVOURITES) : undefined}
    >
      {quickAccessItems.length > 0 ? (
        <ul className="cc-grid cc-grid--quick">{quickAccessItems.map((item) => renderTile(item, "quick"))}</ul>
      ) : (
        <p className="cc-empty">Nothing pinned yet. Tap “Manage”, then the star on a portal to add it here.</p>
      )}
    </Section>
  );

  return (
    <div className="cc-view" key={activeCategory}>
      {activeCategory === CATEGORY.ALL && favoritableCount > 0 && quickAccessSection}

      {activeCategory === CATEGORY.FAVOURITES && (
        <Section id="favourites" title="Favourites" icon={CATEGORY_ICONS[CATEGORY.FAVOURITES]} count={quickAccessItems.length}>
          {quickAccessItems.length > 0 ? (
            <ul className="cc-grid cc-grid--apps">{quickAccessItems.map((item) => renderTile(item, "app"))}</ul>
          ) : (
            <p className="cc-empty">Nothing pinned yet. Tap “Manage”, then the star on a portal to add it here.</p>
          )}
        </Section>
      )}

      {show(CATEGORY.DASHBOARDS) && (
        <Section
          id="dashboards"
          title={CATEGORY_LABELS[CATEGORY.DASHBOARDS]}
          icon={CATEGORY_ICONS[CATEGORY.DASHBOARDS]}
          onViewAll={viewAll(CATEGORY.DASHBOARDS)}
        >
          {list(CATEGORY.DASHBOARDS).length > 0 ? (
            <ul className="cc-grid cc-grid--cards">{list(CATEGORY.DASHBOARDS).map((item) => renderTile(item, "card"))}</ul>
          ) : (
            <p className="cc-empty">No dashboards are assigned to your account.</p>
          )}
        </Section>
      )}

      {[CATEGORY.PORTALS, CATEGORY.PAGES, CATEGORY.ADMIN].map(
        (category) =>
          show(category) &&
          list(category).length > 0 && (
            <Section
              key={category}
              id={category}
              title={CATEGORY_LABELS[category]}
              icon={CATEGORY_ICONS[category]}
              onViewAll={viewAll(category)}
            >
              <ul className="cc-grid cc-grid--apps">{list(category).map((item) => renderTile(item, "app"))}</ul>
            </Section>
          )
      )}
    </div>
  );
};

export default WorkspaceSections;
